import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import IziToast from '../IziToast';

const MODE_OPTIONS = ['Cash', 'M-Pesa', 'Bank', 'Other'];
const VALID_FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const MAX_FILE_BYTES = 1024 * 1024; // matches the backend's fileUploader cap

const currency = 'KES';
const moneyOpts = { maximumFractionDigits: 2 };
const formatMoney = (n) => `${currency} ${(Number(n) || 0).toLocaleString(undefined, moneyOpts)}`;
// Mirrors the backend's helper.round2 — caps at 2dp so JS floating-point
// drift in an upstream balance (e.g. 9.999999999999986 instead of 10)
// never leaks into the pre-filled amount input or the submit comparison.
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Shared "Pay" modal — used across Invoices (Rent), Water Bill, Extra
 * Charges and Service Charge. The two backends it can target
 * (tenant_payments vs owner_payments) expect different identifying
 * fields, so the caller supplies `buildFormData` to translate this
 * modal's generic {amount, mode, transactionDetails, file} capture into
 * the right POST body for its endpoint.
 */
const PayModal = ({
  row, title, subtitle, maxAmount, endpoint, requestConfig,
  buildFormData, onClose, onSuccess,
}) => {
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState(MODE_OPTIONS[0]);
  const [transactionDetails, setTransactionDetails] = useState('');
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Clean, rounded balance — used for the pre-filled amount, the submit
  // comparison, and the "Outstanding balance" hint, so a raw dirty prop
  // (e.g. 9.999999999999986) never surfaces anywhere in this modal.
  const cleanMaxAmount = maxAmount != null ? round2(maxAmount) : null;

  // Reset the form every time a new row is opened (row identity changes
  // on each "Pay" click, including re-opening for the same row).
  useEffect(() => {
    if (row) {
      setAmount(cleanMaxAmount > 0 ? String(cleanMaxAmount) : '');
      setMode(MODE_OPTIONS[0]);
      setTransactionDetails('');
      setFile(null);
      setIsSubmitting(false);
    }
  }, [row]);

  if (!row) return null;

  const close = () => {
    if (isSubmitting) return; // don't allow close mid-flight
    onClose();
  };

  const onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ''; // eslint-disable-line no-param-reassign
    if (!f) return;
    if (!VALID_FILE_TYPES.includes(f.type)) {
      IziToast('Only JPEG, PNG or PDF files are allowed');
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      IziToast('File must be under 1MB');
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      IziToast('Enter a positive amount');
      return;
    }
    if (cleanMaxAmount != null && amt > cleanMaxAmount) {
      IziToast(`Amount cannot exceed the outstanding balance of ${formatMoney(cleanMaxAmount)}`);
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = buildFormData({
        row, amount: amt, mode, transactionDetails, file,
      });
      await axios.post(endpoint, formData, {
        headers: {
          ...(requestConfig && requestConfig.headers),
          'Content-Type': 'multipart/form-data',
        },
      });
      IziToast('Payment recorded');
      onSuccess();
    } catch (e) {
      const msg = (e.response && e.response.data && e.response.data.error)
        || 'Failed to record payment';
      IziToast(msg);
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="presentation"
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="presentation"
        style={{
          background: '#fff',
          width: 'min(440px, 92vw)',
          borderRadius: 6,
          boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #eee' }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        <div style={{ padding: '14px 20px' }}>
          <div className="form-group">
            <label className="control-label">Mode of Payment</label>
            <select
              className="form-control"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              {MODE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="control-label">Amount (KES)</label>
            <input
              type="number"
              min="0"
              step="any"
              className="form-control"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {cleanMaxAmount != null && (
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                Outstanding balance: {formatMoney(cleanMaxAmount)}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="control-label">Transaction Details (optional)</label>
            <input
              type="text"
              className="form-control"
              value={transactionDetails}
              onChange={(e) => setTransactionDetails(e.target.value)}
              placeholder="e.g. M-Pesa code, cheque no., bank ref"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="control-label">Proof of Payment (optional)</label>
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              onChange={onFileChange}
            />
            {file && (
              <div style={{ fontSize: 12, color: '#27ae60', marginTop: 4 }}>
                <i className="fa fa-paperclip" style={{ marginRight: 4 }} />
                {file.name}
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={close}
            disabled={isSubmitting}
            style={{
              border: '1px solid #ccc',
              background: '#fff',
              padding: '6px 16px',
              borderRadius: 4,
              fontSize: 13,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={isSubmitting}
            style={{
              border: '1px solid var(--red_color, #d63835)',
              background: 'var(--red_color, #d63835)',
              color: '#fff',
              padding: '6px 16px',
              borderRadius: 4,
              fontSize: 13,
              fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Saving…' : 'Save Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

PayModal.propTypes = {
  row: PropTypes.objectOf(PropTypes.any),
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  maxAmount: PropTypes.number,
  endpoint: PropTypes.string.isRequired,
  requestConfig: PropTypes.objectOf(PropTypes.any),
  buildFormData: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

PayModal.defaultProps = {
  row: null,
  subtitle: '',
  maxAmount: null,
  requestConfig: {},
};

export default PayModal;
